import React from 'react';

/**
 * SelectField - Dropdown select field
 *
 * @param {Object} props
 * @param {Object} props.field - Field configuration
 * @param {string} props.field.key - Unique field identifier
 * @param {string} props.field.label - Display label
 * @param {Array} props.field.options - Array of options
 *   - Can be strings: ['option1', 'option2']
 *   - Or objects: [{ value: 'opt1', label: 'Option 1' }]
 * @param {any} [props.field.default] - Default value
 * @param {string} [props.field.description] - Optional description text
 * @param {any} props.value - Current value
 * @param {Function} props.onChange - Callback when value changes
 * @param {boolean} [props.disabled] - Whether field is disabled
 * @param {string} [props.error] - Error message to display
 * @param {boolean} [props.compact] - Use compact inline layout
 */
export default function SelectField({ field, value, onChange, disabled = false, error, compact = false }) {
  const handleChange = (e) => {
    onChange(e.target.value);
  };

  const options = field.options || [];

  const selectElement = (
    <select
      value={value ?? field.default ?? ''}
      onChange={handleChange}
      disabled={disabled}
      className={`w-full px-2 py-1 text-xs border rounded bg-gray-50 ${
        error
          ? 'border-red-500 focus:ring-red-500'
          : 'border-gray-200 focus:border-blue-400 focus:ring-1 focus:ring-blue-400'
      } ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
    >
      {options.map((option) => {
        const optValue = typeof option === 'string' ? option : option.value;
        const optLabel = typeof option === 'string' ? option : option.label;
        return (
          <option key={optValue} value={optValue}>
            {optLabel}
          </option>
        );
      })}
    </select>
  );

  // Compact mode: inline label + control
  if (compact) {
    return (
      <div className="flex items-center gap-2 py-0.5">
        <label className="text-xs text-gray-500 w-16 flex-shrink-0 truncate">
          {field.label}
        </label>
        <div className="flex-1 min-w-0">
          {selectElement}
        </div>
      </div>
    );
  }

  // Standard mode: stacked layout
  return (
    <div>
      <label className="block text-xs font-medium text-gray-700 mb-1">
        {field.label}
      </label>
      {selectElement}
      {field.description && (
        <p className="mt-1 text-xs text-gray-500">{field.description}</p>
      )}
      {error && (
        <p className="mt-1 text-xs text-red-500">{error}</p>
      )}
    </div>
  );
}
